import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleFilledIcon, DotCalendarIcon, MapPinIcon, SpinnerIcon} from '../../icons';
import { mapStepToMessage, mapStepToText } from '../../utils/helper';
import { CustomModal } from '../ui';
import CircularStepper from '../orderTracking/CircularStepper';
import { AddDriverDashboardModal } from './addDriverDashboardModal';
import { cfmsCodesOrderTracking, orderSteps } from '../../config/constants';
import { BookingInitButton } from '../../pages/delivery/components/BookingInitButton';
import { Button } from '../ui/Button';
import { BookingCancelButton } from '../../pages/delivery/components/BookingCancelButton';
import { useGetDeliveryStatus } from '../../hooks/react-query/useVehicleDelivery';
import usePersonStore from '../../store/personStore';
import {useGetCarByPlate} from '../../hooks/react-query/useCarData'

const OrderTrackingCorporateView = ({ orderData, carList }) => {

  const navigate = useNavigate();
  const assignedRoles = usePersonStore((store) => store.userRoles);
  const hasOnlyDriversRole =
    assignedRoles.length === 1 && assignedRoles.includes('driver');

  // const leasedCars = carList?.pages?.flatMap(page => page.data) || [];
  // const orderCarData = { ...orderData, ...leasedCars?.find((item) => item?.plateno === orderData?.vehPlate || item.cncode === orderData.cncode)}
  
  const leasedCars = useMemo(() => {
    return carList?.pages?.flatMap((page) => page?.data || []) || [];
  }, [carList?.pages]);
  
  
  const matchedLeasedCar = useMemo(() => {
    if (!orderData) return undefined;
    
    const plate = orderData?.vehPlate;
    const cncode = orderData?.cncode;
    
    if (!plate && !cncode) return undefined;
    
    return leasedCars.find(
      (item) => item?.plateno === plate || item?.cncode === cncode
    );
  }, [orderData, leasedCars]);

  const enableCarInfoGet = orderData?.quantity <2 && 
        !!orderData?.orderLine && 
        ["1005","1006"].includes(orderData?.orderLine?.code) && 
        (!("hasAssignedDriver" in (matchedLeasedCar ?? {})) || !("isDeliveryEligible" in (matchedLeasedCar ?? {})))


  const carPlateNumber = orderData?.vehPlate ?? matchedLeasedCar?.plateno ?? "";
  const { data: carInfo, isLoading: isCarInfoLoading } = 
    useGetCarByPlate({
      searchTerm: carPlateNumber, 
      enableCar:enableCarInfoGet
    });
  
  const orderCarData = useMemo(() => {
    const ordercar = {
        ...(orderData ?? {}),
        ...(matchedLeasedCar ?? {}),
      }
      if(!("hasAssignedDriver" in ordercar) || !("isDeliveryEligible" in ordercar )){
        return {
          ...(orderData || {}),
          ...(carInfo?.data?.[0] || {}),
        };
      } else {
        return ordercar;
      }
  }, [orderData, matchedLeasedCar, carInfo]);

  const { state, title, cncode, orderLine, quantity } = orderCarData || {};
  const { status, message, step, substatus } = state || {};
  
  const [otModalOpen, setOtModalOpen] = useState(false)
  const [stepC, setStepC] = useState(cfmsCodesOrderTracking[orderLine?.code])
  
  const enableDeliveryStatusGet = !!carPlateNumber && ["1006", "1007",].includes(orderLine?.code) 
  const { data: deliveryInfo, isLoading: isDeliveryInfoLoading, } = useGetDeliveryStatus({plateno: carPlateNumber, enabled:enableDeliveryStatusGet})

  const listRef = useRef(null);

  const scrollToActiveCentered = (behavior = "smooth") => {
    const container = listRef.current;
    if (!container) return;

    const activeEl = container.querySelector(`#step-${stepC}`);
    if (!activeEl) return;

    const targetTop =
      activeEl.offsetTop - container.clientHeight  + activeEl.clientHeight / 2;

    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior,
    });
  };

  const deliveryType = useMemo(()=>{
    if(!!deliveryInfo?.data?.currentlyAllowed){
      return "NEW WAY"
    } else {
      if(deliveryInfo?.data?.deliveryLocation?.garageCategory === "Vehicle Delivery - Επαρχία") {
        return "RAC"
      } else if (deliveryInfo?.data?.deliveryLocation?.garageCategory === "Vehicle Delivery - Dealers") {
        return "AUTHORIZED"
      } else {
        return null
      }
    }
  },[deliveryInfo])
  

  const canShowInit = useMemo(()=>{
    return stepC === 7 &&
    !isDeliveryInfoLoading &&
    ((enableCarInfoGet && !isCarInfoLoading) || !enableCarInfoGet) &&
    ((deliveryType === "NEW WAY" && deliveryInfo?.data?.currentlyAllowed === true) || ["AUTHORIZED","RAC"].includes(deliveryType)) &&
    deliveryInfo?.data?.currentlyScheduled !== true &&
    orderCarData?.hasAssignedDriver === true
  },[deliveryInfo?.data?.currentlyAllowed, deliveryInfo?.data?.currentlyScheduled, deliveryType, enableCarInfoGet, isCarInfoLoading, isDeliveryInfoLoading, orderCarData?.hasAssignedDriver, stepC])
  
  const availableDate = useMemo(()=>{
    const isAvailDateFromOrderLineValid = orderCarData?.orderLine?.AvailDate !== "1900-01-01" && !!orderCarData?.orderLine?.AvailDate
    return isAvailDateFromOrderLineValid ? orderCarData?.orderLine?.AvailDate : !!deliveryInfo?.data?.availableFrom ? new Date(deliveryInfo?.data?.availableFrom).toISOString().split("T")[0] : undefined
  },[deliveryInfo?.data?.availableFrom, orderCarData?.orderLine?.AvailDate])

  useEffect(() => {
    setStepC(cfmsCodesOrderTracking[orderLine?.code]);

    if(deliveryInfo?.data?.currentlyScheduled) setStepC(8)

    //We manually set step 4 for multiple orders becuse they do not have a cfms code assigned
    if(quantity > 1 && (orderLine?.code === '' || orderLine?.code === null)) setStepC(4)

  }, [deliveryInfo?.data?.currentlyScheduled, orderLine?.code, quantity]);


  // When modal opens: wait for DOM + layout
  useEffect(() => {
    if (!otModalOpen) return;

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        stepC > 1 && scrollToActiveCentered("smooth"); // "auto" on open feels snappier; change to "smooth" if you want
      });
    });

    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [otModalOpen, stepC]);

  // When step changes while modal is open: re-center
  useEffect(() => {
    if (!otModalOpen) return;
    scrollToActiveCentered("smooth");
  }, [stepC, otModalOpen]);

  return (
    <div className="max-lg:hidden flex flex-col bg-white justify-between min-h-[70vh] p-6 rounded-lg">
      <div className="flex flex-col gap-y-8 ">
        <div className="flex flex-col gap-6">
          <h3>{title}</h3>
        </div>
        {status && step < 7 && (
          <div className="flex flex-col gap-2">
            <span className="text-label text-fadedgrey">Σε εκκρεμότητα</span>
            <div className="text-body">{mapStepToText(step)}</div>
          </div>
        )}
        {message && step < 7 && (
          <div className="text-body">
            {mapStepToMessage(step, substatus) || message}
          </div>
        )}
        <div className="flex flex-col gap-4 pb-2">
          {/* {orderCarData?.FuelType && <div className='flex flex-row gap-[6px]'>
            <GasPump />
            <p className='text-sm text-black'>{orderCarData?.FuelType}</p>
          </div>}
          {orderCarData?.BodyType && <div className='flex flex-row gap-[6px]'>
            <CarIcon className='w-[16px] h-[16px]' color='black'/>
            <p className='text-sm text-black'>{orderCarData?.BodyType}</p>
          </div>} */}
          {orderCarData?.quantity > 1 && <div>
            <p className='text-xs text-fadedgrey'>Ποσότητα παραγγελίας</p>
            <p className='text-sm text-black'>{orderCarData?.quantity} αυτοκίνητα</p>
          </div>}
          {orderCarData?.cncode && <div>
            <p className='text-xs text-fadedgrey'>Κωδικός προσφοράς</p>
            <p className='text-sm text-black'>{orderCarData?.cncode}</p>
          </div>}
          {orderCarData?.StartDate && <div>
            <p className='text-xs text-fadedgrey'>Ημερομηνία έναρξης συμβολαίου</p>
            <p className='text-sm text-black'>{orderCarData?.StartDate?.split('-')[2] + '/' +orderCarData?.StartDate?.split('-')[1] + '/' + orderCarData?.StartDate?.split('-')[0]}</p>
          </div>}
          {orderCarData?.EndDate && <div>
            <p className='text-xs text-fadedgrey'>Ημερομηνία λήξης συμβολαίου</p>
            <p className='text-sm text-black'>{orderCarData?.EndDate?.split('-')[2] + '/' +orderCarData?.EndDate?.split('-')[1] + '/' + orderCarData?.EndDate?.split('-')[0]}</p>
          </div>}
         {orderCarData?.VIN && <div>
            <p className='text-xs text-fadedgrey'>VIN</p>
            <p className='text-sm text-black'>{orderCarData?.VIN}</p>
          </div>}
          {quantity < 2 && orderCarData?.CreditFacilityCode && <div>
            <p className='text-xs text-fadedgrey'>Αριθμός παραρτήματος</p>
            <p className='text-sm text-black'>{orderCarData?.CreditFacilityCode}</p>
          </div>}
          {orderCarData?.vehPlate && (orderCarData?.quantity > 1 ? <div>
            <p className='text-xs text-fadedgrey'>Πινακίδα</p>
            {orderCarData?.vehPlate?.map((plate) => {
              return <p className='text-sm text-black'>{plate}</p>
            })}
          </div> : <div>
            <p className='text-xs text-fadedgrey'>Πινακίδα</p>
            <p className='text-sm text-black'>{orderCarData?.vehPlate}</p>
          </div>)}
          {quantity < 2 && <div className="max-lg:hidden h-[1px] w-full bg-[#D9D9D9]" />}
          {quantity < 2 && stepC && stepC<9 && orderLine && <div className='flex flex-col'>
              <p className='text-xs text-fadedgrey'>Εξέλιξη παραγγελίας - Βήμα {stepC}/8</p>
              <p className='text-sm text-black'>{orderSteps.find(item => item.step === stepC)?.title}</p>
          </div>}
          {quantity < 2 && stepC < 7 && orderLine?.DeliverDate !== null && orderLine?.DeliverDate !== undefined && orderLine?.DeliverDate !== "1900-01-01" && stepC > 1 && <div>
            <div className='flex flex-row gap-[6px]'>
              <DotCalendarIcon className={' w-4 h-4'} />
              <p className='text-xs text-fadedgrey'>Εκτιμώμενη παράδοση</p>
            </div>
            <p className='text-sm text-black'>
              {(() => {
                      const [y, m, d] = orderLine.DeliverDate.split("-");
                      return `${d}.${m}.${y}`;
                    })()}
              </p>
          </div>}
          {quantity < 2 && stepC === 7 && availableDate !== null && availableDate !== undefined && availableDate !== "1900-01-01" && stepC > 1 && <div>
            <div className='flex flex-row gap-[6px]'>
              <DotCalendarIcon className={' w-4 h-4'} />
              <p className='text-xs text-fadedgrey'>Παραλαβή από</p>
            </div>
            <p className='text-sm text-black'>
              {(() => {
                      const [y, m, d] = availableDate?.split("-");
                      return `${d}.${m}.${y}`;
                    })()}
              </p>
          </div>}
          {quantity < 2 && stepC === 8 && deliveryInfo?.data?.currentlyScheduled && !!deliveryInfo?.data?.appointment && 
          // <div>
          //   <div className='flex flex-row gap-2 w-full justify-start items-center'>
          //     <DotCalendarIcon className={'w-4 h-4'}/>
          //     <p className='text-sm font-bold'>
          //       {new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
          //     </p>
          //     <p className='text-sm'>
          //         Ημ/νία παραλαβής
          //     </p>
          //   </div>
          //   {deliveryInfo?.data?.deliveryFlow !== null && <div className='w-full flex flex-row justify-center'>
          //     <BookingCancelButton isRetail={true} flowId={deliveryInfo?.data?.deliveryFlow?.id} carPlate={carPlateNumber} appointment={deliveryInfo?.data?.appointment}/>
          //   </div>}
          // </div>

          <div className='flex flex-col justify-start w-full'>
            <p className='text-xs text-fadedgrey'>
              Ημ/νία παραλαβής
            </p>
            <div className='flex flex-row w-full justify-between gap-2 items-center'>
              <div className='flex flex-row items-center gap-2'>
                <div className='w-6 h-6 rounded-full bg-white flex justify-center items-center'>
                  <DotCalendarIcon className={'w-4 h-4'}/>
                </div>
                <span className='flex flex-row gap-2'>
                  <p className='text-sm font-bold'>
                    {new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                  </p>
                  <p className='text-sm'>
                    {new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleTimeString('el-GR',{
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                      timeZone: 'Europe/Athens',
                    })}
                  </p>
                </span>
              </div>
              {deliveryInfo?.data?.deliveryFlow !== null && 
                <BookingCancelButton isRetail={true} flowId={deliveryInfo?.data?.deliveryFlow?.id} carPlate={carPlateNumber} appointment={deliveryInfo?.data?.appointment}/>
              }
            </div>
          </div>
          }
          {/* {quantity < 2 && stepC === 8 && deliveryInfo?.data?.currentlyScheduled && !!deliveryInfo?.data?.appointment && <>
            <div>
              <div className='flex flex-row gap-2 w-full justify-start items-center'>
                <MapPinIcon className={'w-4 h-4'}/>
                <p className='text-xs text-fadedgrey'>
                  Τοποθεσία παράδοσης
                </p>
              </div>
              <p className='text-sm'>
                  {deliveryInfo?.data?.deliveryLocation?.address}
              </p>
            </div>
          </>} */}
        </div>
      </div>
      <div className='flex flex-col gap-2'>
        {!orderData?.isLegacy && orderData?.source === 'intermediate' && <button
            type="button"
            disabled={!cncode}
            className="btn-primary text-primary-button w-full text-center"
            onClick={() => navigate(`/track-your-car/details/${cncode}`)}
          >
            Ολοκλήρωση
          </button>
        }
        {/* New vehicles implementation */}
        {canShowInit && <BookingInitButton disabled={false} carInfo={orderCarData} deliveryInfo={deliveryInfo?.data}/>}

        {!hasOnlyDriversRole && !isDeliveryInfoLoading && !isCarInfoLoading && (orderCarData?.plateno || orderCarData?.vehPlate) && stepC > 5 && stepC < 9 && !orderCarData?.hasAssignedDriver && <AddDriverDashboardModal plateno={orderCarData?.plateno || orderCarData?.vehPlate} text={"Όρισε κύριο οδηγό"}/>}
        
        {!isDeliveryInfoLoading && deliveryInfo?.data?.currentlyScheduled && <Button
          className="w-full p-3 text-sm text-white font-bold bg-[#D4002A] rounded-md flex gap-2 items-center justify-center"
          onClick={()=>{
              const googleMapsUrl = `https://www.google.com/maps?q=${deliveryInfo?.data?.deliveryLocation?.coordinates?.lat},${deliveryInfo?.data?.deliveryLocation?.coordinates?.lng}`
              window.open(deliveryInfo?.data?.deliveryLocation?.googleMapsURL ?? googleMapsUrl, "_blank", "noopener,noreferrer");           
            }}
        >
          <p className={`text-label ${!deliveryInfo?.data?.deliveryLocation?.googleMapsURL && 'text-fadedgrey'} max-lg:text-[#D4002A]`}>Δες το στον χάρτη</p>
        </Button>}
        {( (enableDeliveryStatusGet && isDeliveryInfoLoading) || (enableCarInfoGet && isCarInfoLoading)) && <div className='w-full flex justify-center items-center'>
          <SpinnerIcon className="w-4 h-4 fill-primary" />
        </div>}

        {stepC < 9  && step > 6 && <CustomModal
          triggerComponent={
            <div className={`w-full p-3 text-sm text-black font-semibold bg-white border-[1px] border-fadedgrey rounded-md`}>
              Δες λεπτομέρειες
            </div>
          }
          wrapperClass="max-h-[600px] h-[920px]"
          handleClick={setOtModalOpen}
          openModal={otModalOpen}
        >
        <div className="h-full lg:px-10 px-5 pt-5 lg:pt-[40px] lg:pb-7">
          <div className="h-full flex flex-col items-center gap-3 l:gap-6">
              <p className="text-center text-[32px] font-bold text-black">Εξέλιξη παραγγελίας</p>
              <div className='h-full flex flex-row gap-16 justify-center items-center pt-[28px] pb-[38px]'>
                <CircularStepper
                  step={quantity < 2 ? stepC : 4}
                  total={8}
                  size={303}
                  strokeWidth={10}
                  startColor="#590113"
                  endColor="#D4002A"
                  text={orderSteps.find(item => item.step === stepC)?.title}
                  carInfo={orderData}
                  showCar={true}
                />
              <div ref={listRef}
                className="h-full flex flex-col gap-3 overflow-auto max-h-[335px] min-w-[444px] pr-9"
              >
                {quantity < 2 ? (
                  orderSteps?.map((step) => {
                    const isActive = stepC === step?.step;

                    return isActive ? (
                      <div
                        key={`#step-${step.step}`}
                        id={`step-${step.step}`}
                        className="flex flex-col gap-4 justify-start"
                      >
                        <div className="flex flex-row gap-2 items-center">
                          <p className="text-base text-white bg-black rounded-full px-4 py-[6px]">
                            Βήμα {stepC}
                          </p>
                        </div>

                        <div className="flex flex-row gap-2 items-center">
                          {step?.icon}
                          <p className="text-2xl font-bold text-black">{step?.title}</p>
                        </div>

                        {/* <p className="text-lg text-black">
                          {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                        </p> */}

                        {stepC === 7 && deliveryInfo?.data?.cancelledButPaid ?
                         <p className="text-lg text-black">Για να προγραμματίσετε εκ νέου το ραντεβού παραλαβής, επικοινωνήστε άμεσα με το Τμήμα Παράδοσης Αυτοκινήτων μέσω e-mail στο car-delivery@avis.gr.</p>
                        : hasOnlyDriversRole && stepC === 7 
                          ? <p className="text-lg text-black">Προγραμμάτισε το ραντεβού για την παραλαβή του αυτοκινήτου σου.</p>
                          : <p className="text-lg text-black">
                              {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                            </p>}

                        {/* {hasOnlyDriversRole && stepC === 7 
                          ? <p className="text-lg text-black">Προγραμμάτισε το ραντεβού για την παραλαβή του αυτοκινήτου σου.</p>
                          : <p className="text-lg text-black">
                              {step?.subtitle} {stepC === 8 && new Date(deliveryInfo?.data?.appointment?.ScheduledDate).toLocaleDateString('el-GR')}
                            </p>
                        } */}

                        {stepC === 7 &&
                        availableDate !== null &&
                        availableDate !== undefined &&
                        availableDate !== "1900-01-01" && (
                          <p className="text-xs text-grenite">
                            {`${step?.caption} `}
                            {stepC > 1 &&
                              (() => {
                                const [y, m, d] = availableDate?.split("-");
                                return `${d}/${m}/${y}`;
                              })()}
                          </p>
                        )}

                        {stepC < 7 &&
                          orderData?.orderLine?.DeliverDate !== null &&
                          orderLine?.DeliverDate !== undefined &&
                          orderData?.orderLine?.DeliverDate !== "1900-01-01" && (
                            <p className="text-xs text-grenite">
                              {`${step?.caption} `}
                              {stepC > 1 &&
                                (() => {
                                  const [y, m, d] = orderLine.DeliverDate.split("-");
                                  return `${d}/${m}/${y}`;
                                })()}
                            </p>
                          )}

                        {stepC < 8 && <div className="max-lg:hidden h-[1px] w-full bg-[#D9D9D9]" />}
                      </div>
                    ) : (
                      <div key={step.step} className="flex flex-col">
                        <div className="flex flex-row gap-4 items-center">
                          <div
                            className={`flex justify-center items-center rounded-full h-8 w-8 text-base ${
                              step?.step < stepC ? "bg-[#FEF2F2] text-primary" : "bg-[#F5F5F7]"
                            }`}
                          >
                            {step?.step}
                          </div>

                          {step?.step < stepC && (
                            <CheckCircleFilledIcon className="h-3 w-3 -ml-[25px] -mb-4" />
                          )}

                          <p className={`text-lg ${step?.step > stepC ? "text-[#282828]" : "text-fadedgrey"}`}>
                            {step?.title}
                          </p>
                        </div>

                        <div className="h-[1px] w-full bg-[#D9D9D9] mt-4" />
                      </div>
                    );
                  })
                ) : (
                  <div className="h-full flex flex-col gap-4 justify-center items-start">
                    <h3>Σε εξέλιξη</h3>
                    <p className="text-lg">
                      Η παραγγελία σου βρίσκεται σε εξέλιξη. Με την ολοκλήρωση της ταξινόμησης, θα λάβεις νέα ενημέρωση μέσω email.
                    </p>
                    <p className="text-xs text-gray-800 leading-tight pt-2">Ποσότητα παραγγελίας: {quantity} αυτοκίνητα</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        </CustomModal> }
      </div>
    </div>
  );
};

export const OrderTrackingCorporateViewMobile = ({ orderData }) => {
  const navigate = useNavigate();

  const { state, requestDate, title, cncode, orderLine } = orderData || {};
  const { status, message, step, substatus } = state || {};

  return (
    <div className="lg:hidden fixed bottom-0 w-full h-fit bg-white flex flex-col items-center text-center gap-3 p-3">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col justify-between">
          <h3>{title}</h3>
          {!orderData?.isLegacy && orderData?.source === 'intermediate' && <button
            type="button"
            disabled={!cncode}
            className="text-link text-primary"
            onClick={() => navigate(`/track-your-car/details/${cncode}`)}
          >
            Ολοκλήρωση
          </button>
          }
        </div>
        {status && step < 7 ? (
            <div className="flex flex-col gap-2">
              <span className="text-label text-fadedgrey"> Σε εκκρεμότητα</span>
              <div className="text-body">{mapStepToText(step)}</div>
            </div>
          ) : <div className="flex flex-col gap-2">
            <p className={`text-[12px] text-fadedgrey`}>
                {orderSteps.find(item => item.step === cfmsCodesOrderTracking[orderLine?.code])?.title}
              </p>
          </div> }
        {step < 7 ? message && (
          <div className="text-body">
            {mapStepToMessage(step, substatus) || message}
          </div>
        ) : <div className='w-full h-full flex flex-col justify-start'>
              <p className={`text-[12px] text-fadedgrey`}>
                {orderSteps.find(item => item.step === cfmsCodesOrderTracking[orderLine?.code])?.subtitle}
              </p>
          </div>}
      </div>
    </div>
  );
};

export default OrderTrackingCorporateView;
